import { BaseService } from './baseService';
import httpClient from './httpClient';
import { parseApiError } from '../utils/errorHandler';

/**
 * Clientes pessoa física — GET/POST/PUT /api/pessoa-fisica
 */
class PhysicalPersonService extends BaseService {
    constructor() {
        super('/api/pessoa-fisica');
    }

    /**
     * @param {Object} params
     * @param {number} [params.page]
     * @param {number} [params.size]
     * @param {string} [params.sort]
     * @param {string|null} [params.name]
     * @param {string|null} [params.phone]
     * @param {string|null} [params.email]
     * @param {string|null} [params.cpf]
     * @param {boolean|null} [params.active]
     * @returns {Promise<{ content: Array, page: Object }>}
     */
    async list({ page = 0, size = 10, sort = 'name,asc', name, phone, email, cpf, active } = {}) {
        const filters = {};
        if (name) filters.name = name;
        if (phone) filters.phone = phone;
        if (email) filters.email = email;
        if (cpf) filters.cpf = cpf;
        if (active === true || active === false) filters.active = active;

        const data = await super.list({ page, size, sort, filters });
        const content = data._embedded?.content ?? data.content ?? [];
        const pageMeta = data.page ?? {
            totalPages: content.length === 0 ? 0 : 1,
            number: page,
            totalElements: content.length,
            first: true,
            last: true,
        };

        return { content, page: pageMeta };
    }

    /**
     * Detalhe do customer PF.
     * GET /api/pessoa-fisica/cliente/{customerId}
     * @param {string|number} customerId
     * @returns {Promise<Object>}
     */
    async getByCustomerId(customerId) {
        const response = await httpClient.get(
            `${this.endpoint}/cliente/${encodeURIComponent(String(customerId))}`,
        );
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }

    /**
     * Pessoa física por person id (não confundir com customerId).
     * GET /api/pessoa-fisica/{id}
     * @param {string|number} personId
     * @returns {Promise<Object>}
     */
    async getByPersonId(personId) {
        return super.get(personId);
    }

    /**
     * Cria cliente PF. Resposta 204 com header Location: /{customerId}
     * @param {Object} payload
     * @returns {Promise<{ id: number|null }>}
     */
    async create(payload) {
        const response = await httpClient.post(this.endpoint, payload);
        if (!response.ok) {
            throw await parseApiError(response);
        }
        const location = response.headers.get('Location');
        let id = null;
        if (location) {
            const parts = String(location).split('/').filter(Boolean);
            const last = parts[parts.length - 1];
            if (/^\d+$/.test(last)) {
                id = Number(last);
            }
        }
        return { id };
    }

    /**
     * Atualiza cliente PF. Resposta 204 com header Location.
     * @param {Object} payload - deve incluir `id` (customerId) para transportadora
     * @returns {Promise<{ id: number|null }>}
     */
    async update(payload) {
        const response = await httpClient.put(this.endpoint, payload);
        if (!response.ok) {
            throw await parseApiError(response);
        }
        const location = response.headers.get('Location');
        let id = payload?.id != null ? Number(payload.id) : null;
        if (location) {
            const parts = String(location).split('/').filter(Boolean);
            const last = parts[parts.length - 1];
            if (/^\d+$/.test(last)) {
                id = Number(last);
            }
        }
        return { id };
    }
}

const physicalPersonService = new PhysicalPersonService();
export default physicalPersonService;
